import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { useId } from 'react'
import { PersonalProfileEditor } from '@/features/tax-profile/individual-profiles/components/PersonalProfileEditor'
import type { PersonalProfileEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalProfileEditorViewModel'
import { ButtonGroup } from '@/components/ButtonGroup'

export interface PersonalProfileEditorDialogProps {
  vm: PersonalProfileEditorViewModel
  isDisabled?: boolean
  testId?: string
  submitButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * A dialog for viewing and editing personal information.
 */
export const PersonalProfileEditorDialog = observer(
  function PersonalProfileEditorDialog({
    vm,
    isDisabled,
    testId,
    submitButtonTestId,
    cancelButtonTestId,
  }: PersonalProfileEditorDialogProps) {
    const formId = useId()
    const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled
    return (
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.close}
        isDismissable={!vm.save.pending}
        sheetSizing="stretch"
        isUsingDialogScrolling
        size="lg"
        testId={testId}
      >
        <ModalDialogHeader>
          {vm.isReadOnly ? (
            <>
              <ModalDialogTitle>Your personal details</ModalDialogTitle>
              <ModalDialogDescription>
                View your personal details below.
              </ModalDialogDescription>
            </>
          ) : vm.isNew ? (
            <>
              <ModalDialogTitle>Add your personal details</ModalDialogTitle>
              <ModalDialogDescription>
                Please complete the following so we can confirm your personal
                information.
              </ModalDialogDescription>
            </>
          ) : (
            <>
              <ModalDialogTitle>Edit your personal details</ModalDialogTitle>
              <ModalDialogDescription>
                View your personal details below, and make changes if needed.
              </ModalDialogDescription>
            </>
          )}
        </ModalDialogHeader>

        <ModalDialogContent>
          <PersonalProfileEditor
            formId={formId}
            isDisabled={isFormDisabled}
            vm={vm}
          />
        </ModalDialogContent>
        <ModalDialogFooter sticky bordered compact>
          <ButtonGroup stretch>
            <Button
              isDisabled={vm.save.pending}
              variant="outline"
              label={vm.isReadOnly ? 'Close' : 'Cancel'}
              onPress={vm.close}
              testId={cancelButtonTestId}
            />
            {!vm.isReadOnly && (
              <Button
                isPending={vm.save.pending}
                isDisabled={isDisabled}
                variant="default"
                label={vm.isNew ? 'Save' : 'Save changes'}
                type="submit"
                form={formId}
                testId={submitButtonTestId}
              />
            )}
          </ButtonGroup>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
